/**
 * Clock poller — OPBridge musical-time snapshot → PWA transport model.
 *
 * Post-migration clock source (NEXT #2, 2026-07-14). Replaces the legacy
 * Node bridge's WS `transport`/`tick` clock role: that path was gated
 * behind `VITE_LEGACY_BRIDGE` (off by default), so before this poller the
 * default build had NO live clock — smoothClock free-ran from a 120 BPM
 * stub. This module gives the default build a real clock via the primary
 * OPBridge.
 *
 * Source: `GET /clock/status` on the PRIMARY bridge (DEC-063 — clock, like
 * engine state, comes from the primary; peers are OPBridge's business).
 * smoothClock interpolates beat/phase at 60fps between updates, so this
 * module only needs to re-anchor often enough to correct drift and to
 * notice transport/tempo changes promptly.
 *
 * Cadence (locked 2026-07-14, idle rate revised 2026-08-23): 4 Hz while
 * playing + fresh, 2 Hz when stopped / stale / no-clock. At 4 Hz the
 * between-anchor drift at 120 BPM is <0.02 beat — imperceptible once
 * interpolated — while staying well under the LAN/battery cost of a 10 Hz
 * poll on the gig iPad.
 *
 * Build 2a, done (2026-08-23 latency pass): DEC-092 filed this as PENDING
 * back on 2026-07-14 — "the marker is built on OPBridge's tree but NOT on
 * the running daemon (404)". Re-checked empirically against the live
 * dev-PC daemon (build v1.15.84+8a1e987) rather than trusting that old
 * note: `clock_marker` frames ARE now flowing over the already-open
 * `/ws/consumer` socket (confirmed live via a raw WS probe, ~1 Hz idle /
 * 4 Hz playing, matching this poller's own cadence). Wired as an
 * ADDITIVE fast path, not a replacement: `onClockMarker()` feeds the same
 * sink the instant a frame arrives, so a transport change (Ableton just
 * pressed play) can land up to a whole poll interval sooner than waiting
 * for this module's own next scheduled tick — the exact "clock felt slow
 * to notice play" complaint the 2026-08-23 idle-cadence halving (below)
 * was already chasing, just closed further. The HTTP poll is untouched
 * and keeps running at the SAME cadence as always — it is the correctness
 * backstop for when the WS is down/reconnecting (DEC-092's own caution:
 * "do NOT retire the poll" was about not trusting the marker as the SOLE
 * source, not about never consuming it). Source label (`clockSourceSig`)
 * stays poll-only — `clock_marker`'s payload carries no `source` field.
 *
 * Idle went from 1 Hz to 2 Hz because pressing play in Ableton was taking
 * up to a full second to be noticed on the PWA (worst case: the idle poll
 * had just fired right before the press) - halving the idle interval halves
 * that worst-case notice lag with a modest LAN cost. The marker fast path
 * above now covers the same gap independently of this interval.
 *
 * The poller owns no UI signals itself; it pushes each snapshot to a sink
 * the app registers at start (App feeds `setTransport` + `clock.anchor`).
 * Silent on 401 / 503 / network — those are expected during the migration
 * window (bearer without `/clock/status` scope, Ableton clock not wired,
 * bridge bouncing). A miss just means "no fresh anchor this tick"; the app
 * falls back to the stub/manual clock exactly as it does when transport
 * goes stale.
 */

import { createSignal, type Accessor } from 'solid-js';
import { bridges, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import { OpbridgeError, type ClockStatus, type ObservedEnum, type ObservedFlag, type OpQuantizeSnapshot } from './httpClient.ts';
import { onClockMarker } from './consumerWs.ts';
import { watchJob } from '../diag/watchdog.ts';

const PLAYING_POLL_INTERVAL_MS = 250; // 4 Hz while playing + fresh
const IDLE_POLL_INTERVAL_MS = 500;    // 2 Hz stopped / stale / no clock (was 1 Hz)

// Latest clock source label from the primary bridge. '' = no clock read
// yet / poll failing / no connected primary. Reactive so the Connect page
// (Settings status card) can show "Clock: Ableton (OSC)" without threading
// the value through App. Not a header pill this session — the BPM "live"
// cue already signals a live clock (decision 2026-07-14).
const [clockSourceSig, setClockSourceSig] = createSignal<string>('');

/**
 * Live's slow-moving OP-AbletonSync settings: record quant, launch quant,
 * metronome, device build.
 *
 * ⚠️ Deliberately NOT part of `ClockSnapshot`, and this is load-bearing.
 * `ClockSnapshot` is rebuilt wholesale on every tick from BOTH sources — the
 * `/clock/status` poll AND the `clock_marker` WS fast path — and the marker
 * frames carry only transport fields. Folding these four into the snapshot
 * would mean every marker between polls overwrote them with `undefined`,
 * blanking the operator's quantization readouts several times a second
 * while the poll briefly restored them. A flickering `--` that nobody could
 * reproduce on demand is a genuinely horrible bug to chase.
 *
 * So: these live here, written ONLY by the poll (the only source that
 * carries them), and read independently of the clock tick.
 *
 * `null` means "not heard yet" and must render as `--`, never as a default —
 * `"0"` is a real quantization label meaning None.
 */
export interface AbletonDeviceState {
  /** Diagnostic only — never gate address support on this (OPBridge
   *  withdrew that advice 2026-08-27; see `ClockStatus.device_version`). */
  deviceVersion: string | null;
  recQuant: ObservedEnum | null;
  launchQuant: ObservedEnum | null;
  metronome: ObservedFlag | null;
  /** The OP-Quantize devices' own report (OPBridge relays it). `null` = never
   *  heard, which a row must not read as a grid. */
  opQuantize: OpQuantizeSnapshot | null;
  /** Wall-clock ms of the last poll that carried these, 0 if never. Lets a
   *  readout grey itself out rather than showing stale settings as current. */
  ts: number;
}

const EMPTY_ABLETON_STATE: AbletonDeviceState = {
  deviceVersion: null,
  recQuant: null,
  launchQuant: null,
  metronome: null,
  opQuantize: null,
  ts: 0,
};

const [abletonStateSig, setAbletonStateSig] = createSignal<AbletonDeviceState>(EMPTY_ABLETON_STATE);

/** Reactive OP-AbletonSync device state. See {@link AbletonDeviceState}. */
export const abletonState: Accessor<AbletonDeviceState> = abletonStateSig;

/**
 * What the primary bridge's clock last REPORTED: tempo, signature and
 * play/stop, from the poll only, with OPBridge's own `fresh` and `source`.
 *
 * For the Ableton Control lights (DEC-291): a "120 BPM" cell lights when Live
 * says 120, a Stop cell while Live says stopped. App's `transport` signal is
 * not enough for that - it is App-local and is written only from FRESH
 * snapshots, so it cannot say "we have stopped hearing". This is `null`
 * whenever there is nothing to believe: no connected primary, a failed read,
 * or a clock that has never reported. Like the source label, a transient
 * network blip keeps the last report rather than flickering every light off.
 *
 * Whether a report describes LIVE is the reader's call (`source` starting
 * `ableton`, the rule the timeline editor uses); `ableton/stepState.ts` makes it.
 */
export interface LiveClockReport {
  source: string;
  tempo: number;
  sigNum: number;
  sigDen: number;
  isPlaying: boolean;
  fresh: boolean;
}

const [liveClockSig, setLiveClockSig] = createSignal<LiveClockReport | null>(null);

/** Reactive. See {@link LiveClockReport}. */
export const liveClockReport: Accessor<LiveClockReport | null> = liveClockSig;

/** Reactive clock-source label. `"ableton_sync"` / `"ableton_osc"` /
 *  `"ableton_link"` / other, or `''` when there's no reading. Read by the
 *  Connect page status card + the header clock-source pill. */
export const clockSource: Accessor<string> = clockSourceSig;

/** Human-facing rendering of a `/clock/status` source value. Kept here so
 *  the Connect page, header pill, and any future surface render it
 *  identically. Live-verified against the running daemon 2026-07-18: the
 *  current M4L clock source reports `ableton_sync` (OP-AbletonSync `/sync`
 *  markers), which superseded the older `ableton_osc` (`/oposc/*`) path —
 *  both are mapped so a rig on either device renders friendly. */
export function clockSourceLabel(source: string): string {
  switch (source) {
    case '':             return 'No clock';
    case 'ableton_sync': return 'Ableton (Sync)';
    case 'ableton_osc':  return 'Ableton (OSC)';
    case 'ableton_link': return 'Ableton (Link)';
    default:             return source; // additive per OPBridge DEC-430
  }
}

/** A clock snapshot delivered to the app. Mirrors what the legacy
 *  `transport` WS event carried, plus the OPBridge source label + fresh
 *  flag so the app can decide whether to anchor and how to display it. */
export interface ClockSnapshot {
  /** `"ableton_osc"` / `"ableton_link"` / other — passed through verbatim. */
  source: string;
  tempo: number;
  /** Continuous extrapolated beat — the smoothClock anchor beat. */
  songTimeBeats: number;
  bar: number;
  beat: number;       // beat-in-bar, 1-indexed
  sixteenth: number;
  isPlaying: boolean;
  sigNum: number;
  sigDen: number;
  /** True when OPBridge considers the clock fresh (a `/oposc/*` packet in
   *  the last 5s AND transport running). When false the app should not
   *  anchor off this snapshot — treat as "no live clock". */
  fresh: boolean;
  /** Wall-clock ms this snapshot was received. The app's stale-check
   *  (Header TRANSPORT_STALE_MS) compares against this. */
  ts: number;
}

export type ClockSink = (snap: ClockSnapshot) => void;

/** The last tempo OPBridge's clock reported, fresh or not, or null before
 *  the first report. A lighting preset with no playing clock runs "at the
 *  clock's last tempo, else 120 BPM" (OPBridge DEC-601), so this is the
 *  number that says how long a timed preset layer really lasts. */
let lastTempo: number | null = null;
export function clockLastTempo(): number | null { return lastTempo; }
function noteTempo(t: unknown): void {
  if (typeof t === 'number' && Number.isFinite(t) && t > 0) lastTempo = t;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;
let sink: ClockSink | null = null;
// Re-entrancy guard: true while a tick's fetch is in flight. Prevents
// pokeClockPoll() from launching a second concurrent tick (which would end
// with two schedule() calls and a doubling timer cascade).
let inFlight = false;
let unsubClockMarker: (() => void) | null = null;

/** Start the poller with a sink. Idempotent — a second call replaces the
 *  sink but doesn't stack timers. */
export function startClockPoll(onSnapshot: ClockSink): void {
  sink = onSnapshot;
  if (unsubClockMarker === null) unsubClockMarker = onClockMarker(handleClockMarker);
  if (timer !== null) return;
  stopped = false;
  schedule(IDLE_POLL_INTERVAL_MS);
}

/** Stop the poller. Used by tests + tear-down. */
export function stopClockPoll(): void {
  stopped = true;
  sink = null;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (unsubClockMarker !== null) { unsubClockMarker(); unsubClockMarker = null; }
}

/** Build 2a fast path — see module doc comment. Fires on every `clock_marker`
 *  frame regardless of the poll's own schedule; never touches `timer` /
 *  `schedule()` / `inFlight`, so the poll's own cadence and fallback
 *  behaviour are completely unaffected by whether the WS is flowing. Silent
 *  on a malformed/incomplete frame (unrecognised future event shape) —
 *  same "a miss just means no fresh anchor this tick" posture as the poll's
 *  own catch block. */
function handleClockMarker(m: Record<string, unknown>): void {
  if (sink === null) return;
  watchJob('clock-marker');
  const songBeats = m['song_beats'];
  const bpm = m['bpm'];
  if (typeof songBeats !== 'number' || typeof bpm !== 'number') return;
  noteTempo(bpm);
  sink({
    source: clockSourceSig(), // marker carries no source field - keep the poll's last reading
    tempo: bpm,
    songTimeBeats: songBeats,
    bar: typeof m['bar'] === 'number' ? m['bar'] : 0,
    beat: typeof m['beat_in_bar'] === 'number' ? m['beat_in_bar'] : 0,
    sixteenth: typeof m['sixteenth'] === 'number' ? m['sixteenth'] : 0,
    isPlaying: m['playing'] === true,
    sigNum: typeof m['sig_num'] === 'number' ? m['sig_num'] : 0,
    sigDen: typeof m['sig_den'] === 'number' ? m['sig_den'] : 0,
    fresh: m['stale'] !== true,
    ts: Date.now(),
  });
}

/**
 * Force an immediate poll instead of waiting out the current interval.
 * Called on PWA foreground/resume (OPBridge clock-design ask, Build 2c):
 * after iPad background-throttling froze rAF + timers, the playhead is stale
 * and the idle cadence could leave it wrong for up to a second. A fresh
 * anchor snaps it back to truth (the resume gap exceeds smoothClock's slew
 * threshold, so it jumps rather than glides — the intended behaviour on
 * wake). No-op if the poller is stopped or a tick is already in flight (that
 * tick will anchor imminently anyway).
 */
export function pokeClockPoll(): void {
  if (stopped || sink === null || inFlight) return;
  if (timer !== null) { clearTimeout(timer); timer = null; }
  void tick();
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped || inFlight) return;
  inFlight = true;
  try {
    await tickInner();
  } finally {
    inFlight = false;
  }
}

async function tickInner(): Promise<void> {
  const cfg = pickPrimaryConnected();
  if (!cfg) {
    // No connected primary — nothing to poll. Clear the source label and
    // stay alive at idle cadence to pick up when the primary comes online.
    setClockSourceSig('');
    setLiveClockSig(null);
    schedule(IDLE_POLL_INTERVAL_MS);
    return;
  }
  watchJob('clock-poll');
  const conn = getConnection(cfg.id);
  if (!conn) {
    setClockSourceSig('');
    setLiveClockSig(null);
    schedule(IDLE_POLL_INTERVAL_MS);
    return;
  }

  let playing = false;
  try {
    const status = await conn.httpClient().clockStatus();
    const snap = translate(status);
    // Source label reflects what OPBridge reports even when stale/stopped
    // (a stopped Ableton is still "Ableton (OSC)"). has_ever_received === false
    // maps to '' so the card reads "No clock" until Ableton first connects.
    setClockSourceSig(status.has_ever_received ? snap.source : '');
    // Device observations ride the poll only (see AbletonDeviceState). Write
    // them on EVERY successful poll, including when `stale` — a stale clock
    // means Ableton stopped sending transport, not that the operator's
    // record-quantization setting stopped being true. `?? null` normalises
    // an older OPBridge's absent field into the same "not heard" value a
    // never-announced property has, so consumers have one case to handle.
    setAbletonStateSig({
      deviceVersion: status.device_version ?? null,
      recQuant: status.rec_quant ?? null,
      launchQuant: status.launch_quant ?? null,
      metronome: status.metronome ?? null,
      opQuantize: status.op_quantize ?? null,
      ts: Date.now(),
    });
    setLiveClockSig(status.has_ever_received
      ? { source: snap.source, tempo: snap.tempo, sigNum: snap.sigNum, sigDen: snap.sigDen, isPlaying: snap.isPlaying, fresh: snap.fresh }
      : null);
    playing = snap.fresh && snap.isPlaying;
    if (sink) sink(snap);
  } catch (err) {
    // 401 (bearer lacks /clock/status scope — shouldn't happen with
    // opbuttons-full, but a manual-URL bridge might), 503 (Ableton clock
    // not wired), network abort — all expected. Skip silently; a miss
    // just means no fresh anchor this tick. The app falls back to the
    // stub/manual clock, same as legacy transport-stale behaviour.
    if (err instanceof OpbridgeError && err.kind !== 'unauthorized') {
      // Don't clear the label on a transient network blip — keep the last
      // known source so the card doesn't flicker "No clock" between ticks.
      // A 401 is different: the bearer genuinely can't read the clock, so
      // clear it.
    } else {
      setClockSourceSig('');
      setLiveClockSig(null);
    }
    playing = false;
  }

  schedule(playing ? PLAYING_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
}

function pickPrimaryConnected(): BridgeConfig | null {
  // DEC-063: clock comes from the primary only.
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  if (conn && conn.status() === 'connected') return primary;
  return null;
}

/** Map OPBridge's `/clock/status` wire shape onto the app's clock
 *  snapshot. `fresh` collapses OPBridge's `stale` flag ONLY - "this
 *  position data is current," not "and also playing." A paused-but-current
 *  snapshot (Ableton transport stopped, OPBridge still reachable) is just
 *  as worth anchoring off as a playing one; `isPlaying` already carries the
 *  play/pause fact separately, and every consumer (transportLive()/
 *  isSlaved()'s staleness check, the header's dot/metronome swap) already
 *  branches on `isPlaying` on its own.
 *
 *  CORRECTED 2026-08-22 - this used to also require `s.playing`, so a
 *  paused snapshot was never applied at all: `transport`'s `ts` stopped
 *  advancing, went stale after 2s, `isSlaved()` flipped false, and a
 *  dormant practice-clock effect (App.tsx, the play/stop effect below
 *  `isSlaved()`) woke up and reset the clock to beat 0 - that effect's
 *  reset-on-stop is correct for an operator stopping LOCAL practice
 *  playback, but wrong here: it fired because the LIVE transport going
 *  stale was indistinguishable from "practice mode, never started,"
 *  wiping a real Ableton pause down to 0.0.0 instead of holding the real
 *  paused position OPBridge kept reporting the whole time. */
function translate(s: ClockStatus): ClockSnapshot {
  noteTempo(s.tempo);
  return {
    source: s.source,
    tempo: s.tempo,
    songTimeBeats: s.beat,
    bar: s.bar,
    beat: s.beat_in_bar,
    sixteenth: s.sixteenth,
    isPlaying: s.playing,
    sigNum: s.signature_num,
    sigDen: s.signature_den,
    fresh: !s.stale,
    ts: Date.now(),
  };
}
